import { registerPlugin } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useUiStore } from '@/store/uiStore';

interface NativeDownloadPluginInterface {
  downloadAndSave(options: {
    base64Data: string;
    filename: string;
    mimeType?: string;
    openImmediately?: boolean;
  }): Promise<{ success: boolean; path?: string; uri?: string }>;
}

const NativeDownload = registerPlugin<NativeDownloadPluginInterface>('NativeDownload');

function getMimeType(filename: string): string {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
  if (lower.endsWith('.csv')) return 'text/csv';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.txt')) return 'text/plain';
  return 'application/octet-stream';
}

export async function writeTextFile(
  path: string,
  data: string,
  directory: Directory = Directory.Cache,
): Promise<string> {
  const result = await Filesystem.writeFile({
    path,
    data,
    directory,
    encoding: Encoding.UTF8,
  });
  return result.uri;
}

export async function writeBase64File(
  path: string,
  base64Data: string,
  directory: Directory = Directory.Cache,
): Promise<string> {
  const result = await Filesystem.writeFile({
    path,
    data: base64Data,
    directory,
  });
  return result.uri;
}

export async function shareLocalFile(uri: string, title?: string): Promise<boolean> {
  try {
    await Share.share({
      title: title || 'Share file',
      files: [uri],
      dialogTitle: title || 'Share file',
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Universal file downloader for Web and Native (Android/iOS).
 * Saves to public Downloads directory on Android and triggers immediate file viewer.
 */
export async function downloadFileBlob(blob: Blob, filename: string): Promise<boolean> {
  if (!blob || !blob.size) {
    useUiStore.getState().showToast('File content is empty', 'error');
    return false;
  }

  const mimeType = blob.type || getMimeType(filename);

  if (Capacitor.isNativePlatform()) {
    try {
      const buffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
      }
      const base64Data = btoa(binary);

      // 1. Android: Use NativeDownload plugin to save directly to public Downloads folder & open viewer
      if (Capacitor.getPlatform() === 'android') {
        try {
          const res = await NativeDownload.downloadAndSave({
            base64Data,
            filename,
            mimeType,
            openImmediately: true,
          });
          if (res?.success) {
            useUiStore.getState().showToast(`Saved ${filename} to Downloads`, 'success');
            return true;
          }
        } catch (nativeErr) {
          console.warn('NativeDownload plugin failed, attempting Filesystem fallback', nativeErr);
        }
      }

      // 2. iOS / Generic Native Fallback: Write via Filesystem and share via FileProvider
      let savedUri = '';
      try {
        const cacheRes = await Filesystem.writeFile({
          path: filename,
          data: base64Data,
          directory: Directory.Cache,
          recursive: true,
        });
        savedUri = cacheRes.uri;
      } catch (cacheErr) {
        console.warn('Cache write fallback', cacheErr);
      }

      if (!savedUri) {
        const docRes = await Filesystem.writeFile({
          path: filename,
          data: base64Data,
          directory: Directory.Documents,
          recursive: true,
        });
        savedUri = docRes.uri;
      }

      useUiStore.getState().showToast(`Downloaded ${filename}`, 'success');

      // Prompt user to open or save the file
      try {
        await Share.share({
          title: filename,
          files: [savedUri],
          dialogTitle: `Open ${filename}`,
        });
      } catch (shareErr) {
        console.log('Share dismissed or completed', shareErr);
      }

      return true;
    } catch (err: any) {
      useUiStore.getState().showToast(err?.message || 'Failed to save file to device', 'error');
      return false;
    }
  }

  // Web Browser environment:
  try {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    return true;
  } catch (err: any) {
    useUiStore.getState().showToast(err?.message || 'Download failed', 'error');
    return false;
  }
}

export async function openPdfBlob(blob: Blob, filename: string): Promise<boolean> {
  return downloadFileBlob(blob, filename);
}

export async function downloadAndSharePdf(
  url: string,
  filename = 'document.pdf',
): Promise<boolean> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return downloadFileBlob(blob, filename);
  } catch {
    if (!Capacitor.isNativePlatform()) {
      await Browser.open({ url });
      return true;
    }
    return false;
  }
}

export async function pickFiles(options?: {
  types?: string[];
  limit?: number;
}): Promise<Array<{ name: string; mimeType?: string; path?: string; blob?: Blob }>> {
  try {
    if (Capacitor.isNativePlatform()) {
      const result = await FilePicker.pickFiles({
        types: options?.types,
        limit: options?.limit ?? 1,
        readData: false,
      });
      return (result.files || []).map((f) => ({
        name: f.name,
        mimeType: f.mimeType,
        path: f.path,
      }));
    }

    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = (options?.limit ?? 1) > 1;
      if (options?.types?.length) input.accept = options.types.join(',');
      input.onchange = () => {
        const files = Array.from(input.files || []).map((f) => ({
          name: f.name,
          mimeType: f.type,
          blob: f,
        }));
        resolve(files);
      };
      input.click();
    });
  } catch {
    return [];
  }
}

export async function saveAndOpenBlob(blob: Blob, filename: string): Promise<boolean> {
  return downloadFileBlob(blob, filename);
}
